import React, { forwardRef } from "react";
import {
  Box,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

interface Service {
  id: number;
  name: string;
  description: string;
  price: string;
}

interface Company {
  name: string;
  company_logo: string | null;
  phone_number: string;
  website: string;
  street_address: string;
  city: string;
  state: string;
}

interface Client {
  id?: number;
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  street_address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}

interface Deceased {
  id: number;
  name: string;
}

interface Package {
  id: number;
  name: string;
  price: string;
  currency: string;
  description: string;
  extra_services: Service[];
  package_name: string;
  package_type: string;
  package_description: string;
}

interface InvoiceDetails {
  invoice_number: string;
  total_price: string;
  created_at: string;
}

interface InvoiceResponse {
  company: Company;
  client: Client;
  deceased: Deceased[];
  package: Package;
  invoice: InvoiceDetails;
}

interface Props {
  invoice: InvoiceResponse;
  
}
const formatInvoiceDate = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleDateString("en-US", { month: "long" });
  const year = date.getFullYear();
  
  return `${day} ${month} ${year}`;
};

const PrintableInvoice = forwardRef<HTMLDivElement, Props>(
  ({ invoice: initialInvoice }, ref) => {
    const getClientDisplayName = (client?: Client | null) => {
      if (!client) return "";

      if (client.name?.trim()) {
        return client.name.trim();
      }

      const firstName = client.first_name?.trim();
      const lastName = client.last_name?.trim();
      return [firstName, lastName].filter(Boolean).join(" ");
    };

    const invoice = {
      ...initialInvoice,
      company: initialInvoice.company || {
        name: "Muzukuru",
        company_logo: null,
        phone_number: "",
        website: "",
        street_address: "",
        city: "",
        state: "",
      },
    };

    const clientName = getClientDisplayName(invoice.client);
    const clientEmail = invoice.client?.email?.trim();
    const clientPhone = invoice.client?.phone?.trim();
    const clientAddress = [
      invoice.client?.street_address?.trim(),
      [invoice.client?.city?.trim(), invoice.client?.state?.trim(), invoice.client?.postal_code?.trim()]
        .filter(Boolean)
        .join(" "),
      invoice.client?.country?.trim(),
    ].filter(Boolean);

    return (
      <Box
        ref={ref}
        sx={{
          backgroundColor: "#fff",
          width: "100%",
          maxWidth: "900px",
          mx: "auto",
          p: 6,
          fontFamily: "'Inter', sans-serif",
          boxSizing: "border-box",
          // Ensures clean print behavior without color/background stripping
          "@media print": {
            p: 0,
            colorAdjust: "exact",
            WebkitPrintColorAdjust: "exact",
          },
        }}
      >
        {/* ================= HEADER ================= */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={6}
        >
          <Box>
            {invoice.company.company_logo ? (
              <Box
                component="img"
                src={invoice.company.company_logo}
                alt={invoice.company.name}
                sx={{
                  width: 200,
                  height: 100,
                  objectFit: "cover",
                  mb: 1,
                }}
              />
            ) : (
              <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
                {invoice.company.name}
              </Typography>
            )}

            <Typography
              fontSize={10}
              color="text.secondary"
              sx={{ letterSpacing: "0.15em", textTransform: "uppercase" }}
            >
              EVENTS | CONFERENCES | FUNERALS
            </Typography>
          </Box>

          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{
                fontSize: 48,
                fontWeight: 300,
                letterSpacing: "-0.05em",
                lineHeight: 1,
                color: "#000000",
                mb: 3,
              }}
            >
              INVOICE
            </Typography>

            <Box sx={{ fontSize: "13px", lineHeight: 1.6, color: "text.primary" }}>
              <Typography sx={{ fontWeight: "bold", fontSize: "inherit" }}>
                {invoice.company.name}
              </Typography>
              <Typography sx={{ fontSize: "inherit" }}>
                {invoice.company.street_address}
              </Typography>
              <Typography sx={{ fontSize: "inherit" }}>
                {invoice.company.city}, {invoice.company.state}
              </Typography>
              <Typography sx={{ fontSize: "inherit" }}>
                {invoice.company.phone_number}
              </Typography>
              <Typography
                sx={{ fontSize: "inherit", fontWeight: 600, mt: 1 }}
              >
                {invoice.company.website}
              </Typography>
            </Box>
          </Box>
        </Stack>

        {/* ================= BILL TO & DETAILS ================= */}
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={6}
          mb={6}
        >
          <Box flex={1} sx={{ fontSize: "14px", lineHeight: 1.5 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 11,
                color: "text.secondary",
                letterSpacing: "0.05em",
                mb: 1,
              }}
            >
              BILL TO
            </Typography>
            {invoice.package?.name && (
              <Typography sx={{ fontWeight: 700, fontSize: "16px", mb: 1.5 }}>
                {invoice.package.name}
              </Typography>
            )}

            {invoice.client ? (
              <>
                {clientName && (
                  <Typography sx={{ fontWeight: 600, fontSize: "15px" }}>
                    {clientName}
                  </Typography>
                )}

                {clientAddress.length > 0 && (
                  <Typography sx={{ mt: 0.5 }}>{clientAddress.join(", ")}</Typography>
                )}

                {clientPhone && <Typography sx={{ mt: 0.5 }}>{clientPhone}</Typography>}

                {!clientName && !clientAddress.length && !clientPhone && !clientEmail && (
                  <Typography color="text.secondary">
                    No client information available.
                  </Typography>
                )}
              </>
            ) : (
              <Typography color="text.secondary">
                No client information available.
              </Typography>
            )}
          </Box>

          <Paper
            elevation={0}
            sx={{
              width: 320,
              border: "1px solid #E5E7EB",
              borderRadius: 2,
              overflow: "hidden",
              fontSize: "13px",
            }}
          >
            <Stack>
              <Box sx={{ display: "flex", justifyContent: "space-between", p: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "inherit" }}>Invoice Number</Typography>
                <Typography sx={{ fontSize: "inherit" }}>#{invoice.invoice.invoice_number}</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: "flex", justifyContent: "space-between", p: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "inherit" }}>Invoice Date</Typography>
                <Typography sx={{ fontSize: "inherit" }}>
                  {formatInvoiceDate(invoice.invoice.created_at)}
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ display: "flex", justifyContent: "space-between", p: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "inherit" }}>Payment Due</Typography>
                <Typography sx={{ fontSize: "inherit" }}>
                  {formatInvoiceDate(invoice.invoice.created_at)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "#F3F4F6",
                  p: 2,
                  borderTop: "1px solid #E5E7EB",
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>Amount Due (USD)</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: "16px" }}>
                  ${invoice.invoice.total_price}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Stack>

                {/* ================= SERVICES TABLE ================= */}
        <TableContainer
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: 1,
            mb: 5,
            overflow: "hidden",
          }}
        >
          <Table sx={{ borderCollapse: "collapse" }}>

            <TableHead>
              <TableRow sx={{ bgcolor: "#3F3F46" }}>

                <TableCell
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "12px",
                  }}
                >
                  Item
                </TableCell>

                <TableCell
                  align="center"
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "12px",
                  }}
                >
                  Qty
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "12px",
                  }}
                >
                  Price
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "12px",
                  }}
                >
                  Amount
                </TableCell>

              </TableRow>
            </TableHead>


            <TableBody>

              {/* PACKAGE ROW */}
              <TableRow>

                <TableCell
                  sx={{
                    width:"55%",
                    py:2,
                  }}
                >

                  <Typography
                    sx={{
                      fontWeight:700,
                      fontSize:"13px",
                    }}
                  >
                    {invoice.package.name}
                  </Typography>


                  <Typography
                    sx={{
                      fontSize:"12px",
                      color:"text.secondary",
                      mt:1
                    }}
                  >
                  
                  </Typography>


                  <Typography
                      component="div"
                      sx={{
                        fontSize: "12px",
                        color: "text.secondary",
                        mt: 1,
                        ml: 2,
                      }}
                      dangerouslySetInnerHTML={{
                        __html: invoice.package.package_description ?? "",
                      }}
                    />

                </TableCell>


                <TableCell align="center">
                  1
                </TableCell>


                <TableCell align="right">
                  ${invoice.package.price}
                </TableCell>


                <TableCell align="right">
                  ${invoice.package.price}
                </TableCell>


              </TableRow>



              {/* ADDITIONAL SERVICES */}
              {(invoice.package?.extra_services ?? []).length > 0 ? (
                (invoice.package?.extra_services ?? []).map((service) => (
                  <TableRow key={service.id}>
                    <TableCell
                      sx={{
                        py: 1.5,
                        pl: 4,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "13px",
                        }}
                      >
                        {service.name}
                      </Typography>

                      {service.description && (
                        <Typography
                            component="div"
                            sx={{
                              fontSize: "11px",
                              color: "text.secondary",
                            }}
                            dangerouslySetInnerHTML={{
                              __html: service.description,
                            }}
                          />
                      )}
                    </TableCell>

                    <TableCell align="center">
                      1
                    </TableCell>

                    <TableCell align="right">
                      ${service.price}
                    </TableCell>

                    <TableCell align="right">
                      ${service.price}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    align="center"
                    sx={{
                      py: 3,
                      color: "text.secondary",
                      fontStyle: "italic",
                    }}
                  >
                    No additional services added.
                  </TableCell>
                </TableRow>
              )}
              {/* TOTAL ROW */}
                <TableRow
                  sx={{
                    bgcolor: "#F9FAFB",
                  }}
                >
                  <TableCell
                    colSpan={3}
                    align="right"
                    sx={{
                      fontWeight: 700,
                      fontSize: "14px",
                      borderTop: "2px solid #E5E7EB",
                    }}
                  >
                    Total
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      fontSize: "14px",
                      borderTop: "2px solid #E5E7EB",
                    }}
                  >
                    ${invoice.invoice.total_price}
                  </TableCell>
                </TableRow>

            </TableBody>

          </Table>

        </TableContainer>

        {/* ================= FOOTER ================= */}
        <Divider sx={{ mb: 3 }} />
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={6}>
          <Box flex={1} sx={{ fontSize: "13px", lineHeight: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "14px", mb: 1 }}>
              Payment Details
            </Typography>
            <Typography sx={{ fontSize: "inherit" }}>NBS Bank</Typography>
            <Typography sx={{ fontSize: "inherit" }}>Hello Zimbabwe (Private) Limited</Typography>
            <Typography sx={{ fontSize: "inherit" }}>Account Number: 3211 013916 451 (USD)</Typography>
            <Typography sx={{ fontSize: "inherit" }}>Branch: Harare Branch</Typography>
            <Typography sx={{ fontSize: "inherit" }}>Branch Code: 32100</Typography>
          </Box>

          <Box flex={1} sx={{ fontSize: "13px", lineHeight: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "14px", mb: 1 }}>
              Contact
            </Typography>
            <Typography sx={{ fontSize: "inherit" }}>{invoice.company.name}</Typography>
            <Typography sx={{ fontSize: "inherit" }}>{invoice.company.phone_number}</Typography>
            <Typography sx={{ fontSize: "inherit" }}>{invoice.company.website}</Typography>
            <Typography sx={{ fontSize: "inherit", mt: 1.5, color: "text.secondary" }}>
              Should you have any questions regarding this invoice, please contact our accounts department.
            </Typography>
          </Box>
        </Stack>

        <Box mt={6}>
          <Divider />
          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>
              Thank you for choosing {invoice.company.name}.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>
              Generated by MMS
            </Typography>
          </Stack>
          <Box display="flex" justifyContent="center" mt={4}>
            <Typography variant="caption" color="text.secondary">
              Invoice #{invoice.invoice.invoice_number}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }
);

PrintableInvoice.displayName = "PrintableInvoice";

export default PrintableInvoice;